import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, getComicsByCategory, getCategoryBySlug, Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const category = slug ? getCategoryBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const comics = slug ? getComicsByCategory(slug) : [];
    let sorted = [...comics];
    if (sortBy === 'popular') {
      sorted.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setFilteredComics(sorted);
  }, [slug, sortBy]);

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category?.name || '分类'} - 星球漫画`,
          description: category?.description || '星球漫画分类浏览',
          url: `https://yzdaohc.cn/category/${slug}`,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类' },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              {category?.name || '分类'}
            </h1>
            <p className="text-sm text-foreground-500 max-w-lg">{category?.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong>{filteredComics.length}</strong> 部漫画
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
              {([
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ] as const).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setSortBy(item.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === item.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block whitespace-nowrap cursor-pointer">返回首页</Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}